import React from 'react';
import VillageCenter from './VillageCenter';
import BuildingRenderer from './BuildingRenderer';
import SkullLantern from '../Props/SkullLantern';
import MeshyModel from '../Props/MeshyModel';
import KenneyModel from '../Props/KenneyModel';
import FireCircle from '../Props/FireCircle';
import DarkMountain from '../Environment/DarkMountain';
import DistantTree from '../Environment/DistantTree';
import LowPolyFence from '../Environment/LowPolyFence';
import LowPolyRiver from '../Environment/LowPolyRiver';
import LowPolyBridge from '../Environment/LowPolyBridge';
import {
  BUILDING_POSITIONS, MOUNTAINS_NEAR, MOUNTAINS_MID, MOUNTAINS_FAR,
  DISTANT_TREES, TORCH_POS, TREE_POSITIONS,
  ROCK_POSITIONS, FENCE_SEGMENTS,
  MESHY_TREE, MESHY_BOARD, MESHY_SKULL, MESHY_RING,
  MESHY_OBELISK,
} from '../constants';
import { faceCenter, mulberry32 } from '../utils';

// Aggregates the full village — center landmarks, buildings, lanterns,
// mountains, trees, rocks, dark props, fences, river + bridge, and
// night-only blood splatter clusters.
//
// gameSeed (int, defaults to 0) drives small procedural variations so
// back-to-back matches in the same lobby don't show the identical
// village: cottage rotations get ±10° of variance, and the first cottage
// the seed lands on gets a slightly larger rotation jitter (up to ±20°)
// so at least one landmark feels clearly different between games. All
// variance is deterministic by seed — every client renders the same
// layout because they all hash the same roomCode + gameStartedAt.
const Village = React.memo(({ isDay, isTrialPhase, gameSeed = 0 }) => {
  const rng = mulberry32(gameSeed || 1);
  // Chapel (index 0) stays rotationally locked — it's THE landmark and
  // its rotation affects trial camera composition. Cottages (index 1+)
  // get variance.
  const buildingVariance = BUILDING_POSITIONS.map((_, i) => {
    if (i === 0) return 0;
    // Most cottages ±10°, one "accent" cottage ±22° for a clearly
    // different silhouette each game.
    const r = rng();
    const isAccent = rng() < 0.12;
    const spread = isAccent ? 0.38 : 0.17; // radians (~22° vs ~10°)
    return (r - 0.5) * 2 * spread;
  });
  return (
  <group>
    <VillageCenter isTrialPhase={isTrialPhase} />

    {BUILDING_POSITIONS.map((b, i) => {
      const jitter = buildingVariance[i];
      const baseRot = b.rotation;
      const rotation = jitter === 0 ? baseRot : [baseRot[0], baseRot[1] + jitter, baseRot[2]];
      return <BuildingRenderer key={`bld-${i}`} {...b} rotation={rotation} />;
    })}

    {TORCH_POS.map((pos, i) => (
      <SkullLantern key={`lantern-${i}`} position={pos} rotation={[0, i * Math.PI / 2, 0]} scale={1.2} />
    ))}

    {/* 3 concentric mountain rings — near detailed, mid taller, far
        gigantic and haze-washed. Tones lighten toward the horizon so the
        mountains feel like they recede into atmospheric perspective. */}
    {MOUNTAINS_NEAR.map((m, i) => (
      <DarkMountain key={`mtn-near-${i}`} position={m.position} scale={m.scale * 1.2} variant={m.variant ?? i} tone="near" />
    ))}
    {MOUNTAINS_MID.map((m, i) => (
      <DarkMountain key={`mtn-mid-${i}`} position={m.position} scale={m.scale * 1.2} variant={m.variant ?? i} tone="mid" />
    ))}
    {MOUNTAINS_FAR.map((m, i) => (
      <DarkMountain key={`mtn-far-${i}`} position={m.position} scale={m.scale * 1.2} variant={m.variant ?? i} tone="far" />
    ))}

    {/* Mid-ground forest ring — procedural firs filling the gap between
        cottages and the near mountains. */}
    {DISTANT_TREES.map((t, i) => (
      <DistantTree key={`dt-${i}`} position={t.position} scale={t.scale} variant={t.variant} isDay={isDay} />
    ))}

    {TREE_POSITIONS.map((pos, i) => (
      <MeshyModel
        key={`tree-${i}`}
        path={MESHY_TREE}
        position={pos}
        scale={1.4 + (i % 4) * 0.3}
        rotation={[0, i * 1.3, 0]}
      />
    ))}

    {ROCK_POSITIONS.map((r, i) => (
      <KenneyModel
        key={`rock-${i}`}
        path="/models/kaykit/rock_single_A.gltf"
        position={r.position}
        scale={r.scale * 3}
        rotation={[0, i * 2.1, 0]}
      />
    ))}

    {/* Extra Meshy trees behind the buildings (radius 20+) */}
    <MeshyModel path={MESHY_TREE} position={[-21, 0, -6]} scale={1.7} rotation={[0, 0.4, 0]} />
    <MeshyModel path={MESHY_TREE} position={[21, 0, -5]} scale={1.6} rotation={[0, 1.1, 0]} />
    <MeshyModel path={MESHY_TREE} position={[-22, 0, 4]} scale={1.8} rotation={[0, 2.3, 0]} />
    <MeshyModel path={MESHY_TREE} position={[22, 0, 5]} scale={1.5} rotation={[0, 0.8, 0]} />
    <MeshyModel path={MESHY_TREE} position={[-9, 0, 21]} scale={1.6} rotation={[0, 1.7, 0]} />
    <MeshyModel path={MESHY_TREE} position={[9, 0, 22]} scale={1.7} rotation={[0, 2.9, 0]} />

    {/* Dark-theme props: wanted board, skull sign, rope ring */}
    <MeshyModel path={MESHY_BOARD}
      position={[5.5, 0, 3]} scale={1.9}
      rotation={[0, faceCenter(5.5, 3), 0]} />
    <MeshyModel path={MESHY_SKULL}
      position={[-5.5, 0, -4]} scale={1.3}
      rotation={[0, faceCenter(-5.5, -4), 0]} />
    <MeshyModel path={MESHY_RING}
      position={[5, 0, -10]} scale={1.6}
      rotation={[0, 0.5, 0]}
      halfHeight={0.38} />

    {/* Wolf obelisk : big monolith in the back-left corner, past the outer
        cottage ring so it breaks the silhouette without crowding the plaza.
        Previous NW plaza spot (-6, 6) at scale 1.5 clipped the SW plaza
        fence and got dwarfed by the bulletin board. */}
    <MeshyModel path={MESHY_OBELISK}
      position={[-16, 0, -14]} scale={2.5}
      rotation={[0, faceCenter(-16, -14), 0]}
      halfHeight={0.95} />

    {/* Runic fire circle : mystical ground feature in the SW plaza, pairs
        narratively with the rope ring across the center. The FireCircle
        wrapper adds embers + pulsing pointLights on top of the GLB (the
        model textures show a lit fire but the scene had no real glow). */}
    <FireCircle position={[-3, 0, -9]} scale={1.3} rotation={[0, 0.8, 0]} />

    {FENCE_SEGMENTS.map((f, i) => (
      <LowPolyFence key={`fence-${i}`} start={f.start} end={f.end} />
    ))}

    <LowPolyRiver isDay={isDay} />
    <LowPolyBridge position={[5, 0, 16]} rotation={[0, 0.15, 0]} scale={1.3} />

    {/* Night-only blood splatter clusters — each cluster is 6 small splats */}
    {!isDay && <>
      {[
        [2, -3], [-3.5, 2], [5, -8], [-8, -5], [0, 5],
        [-1, -1.5], [3, 6], [-6, -9], [8, -6],
      ].map(([cx, cz], ci) => (
        <group key={`blood-cluster-${ci}`}>
          {[
            [0, 0], [0.2, 0.15], [-0.15, 0.2], [0.1, -0.2], [-0.25, -0.1], [0.3, 0.05],
          ].map(([ox, oz], si) => (
            <mesh key={`b-${ci}-${si}`}
              position={[cx + ox, 0.03, cz + oz]}
              rotation={[-Math.PI / 2, 0, (ci * 1.7 + si * 2.3)]}
            >
              <circleGeometry args={[0.06 + (si % 3) * 0.04, 4 + si % 2]} />
              <meshBasicMaterial
                color={si % 2 === 0 ? '#7a1515' : '#5a0e0e'}
                transparent
                opacity={0.55}
                depthWrite={false}
              />
            </mesh>
          ))}
        </group>
      ))}
    </>}
  </group>
  );
});

export default Village;
